import { Injectable } from '@angular/core';
import { INutzer } from '../models/INutzer.interface';
import { BackendService } from './backend.service';
import { SessionService } from './session.service';
import { JsonOf, WriteReturnType } from '../utils/types/backendTypes';
import { IRechnungItem } from '../models/IRechnungItem';
import { IPasskey } from '../models/IPasskey.interface';
import { toCreateOptions, registrationToJson } from '../utils/webauthn';

@Injectable({
  providedIn: 'root'
})
export class NutzerService {

  private nutzer: INutzer[] = [];
  private revision: number = -1;

  public static fromJsonObj<T>(obj: JsonOf<T>): T {
    BackendService.updateIdToProperty(obj, "stamme", (id) => (window as any).stammService.getStamm(id), true, ()=>(window as any).stammService.getRevision());
    BackendService.replaceDate(obj, "geburtsdatum");
    return obj as unknown as T;
  }

  constructor(
    private backend: BackendService,
    private sessionService: SessionService,
  ) {
  }


  /**
   * ID wird nicht mit geschrieben
   * @param nutzer
   */
  public createNutzer(nutzer: INutzer): Promise<string> {
    try {

      const data = this.backend.sendRequest("createNutzer", {
        nutzer: BackendService.toJsonObj(nutzer),
        sessionId: this.sessionService.session.sessionId
      }, true);

      return data.then(msg => {
        return new Promise<string>((resolve, reject) => {
          if (msg.success === false) {
            reject(msg.error);
          }
          resolve("Nutzer erfolgreich angelegt");
        })
      });
    } catch (e) {
      return Promise.reject(e.message);
    }
  }

  public async deleteNutzer(nutzer: INutzer): Promise<string> {
    const response = await this.backend.sendRequest("deleteNutzer", {
      id: nutzer.id,
      sessionId: this.sessionService.session.sessionId
    }, true);
    if(response.success){
      return "Succes";
    }
    throw response.error;
  }

  public async getRechnungen(): Promise<IRechnungItem[]> {
    const response = await this.backend.sendRequest("getRechnungenToUser", {
      sessionId: this.sessionService.session.sessionId
    }, false);

    response.forEach(line => {
      BackendService.updateIdToProperty(line, "stamm", (id) => (window as any).stammService.getStamm(id), false, ()=>(window as any).stammService.getRevision());
      BackendService.updateIdToProperty(line, "veranstaltung", (id) => (window as any).veranstaltungService.getEvent(id), false, ()=>(window as any).veranstaltungService.getRevision());
    });

    return response as unknown as IRechnungItem[];
  }

  public async sendPasswordRecoveryMail(email): Promise<true> {
    const result = await this.backend.sendRequest("sendPasswordRecoveryMail", {email: email}, false);
    return result.ok;
  }

  public async sendUsernameRecoveryMail(email): Promise<true> {
    const result = await this.backend.sendRequest("sendUsernameRecoveryMail", {email: email}, false);
    return result.ok;
  }

  public async setPasswordFromRecovery(username, token, password): Promise<WriteReturnType> {
    const result = await this.backend.sendRequest("setPasswordFromRecovery", {
      username: username,
      token: token,
      password: password
    }, false);
    return result;
  }

  public updateData(): Promise<INutzer[]> {
    const data = this.backend.sendRequest("getNutzer", {
      sessionId: this.sessionService.session.sessionId
    }, false);

    const result = data.then(d => {
      if (d === null) {
        return [];
      }

      return d.map(NutzerService.fromJsonObj);
    }).catch(err => {
      console.log("Failed to load Nutzer");
      console.log(err);
    }).then(d => {
      this.nutzer = d as unknown as INutzer[];
      this.revision++;
      return this.nutzer;
    });
    return result;
  }


  public getNutzerByUsername(username: string): INutzer {
    return this.nutzer.find(n => n.username === username);
  }

  public getNutzer(id: number): INutzer {
    return this.nutzer.find(n => n.id === id);
  }

  public updateNutzerBasics(nutzer: INutzer): Promise<string> {
    try {
      const data = this.backend.sendRequest("updateNutzerBasics", {
        nutzer: BackendService.toJsonObj(nutzer),
        sessionId: this.sessionService.session.sessionId
      }, true);
      return data.then(msg => {
        return new Promise<string>((resolve, reject) => {
          if (msg.success === false) {
            reject(msg.error);
          }
          resolve("Änderungen gespeichert");
        })
      });
    } catch (e) {
      return Promise.reject(e.message)
    }
  }

  public updateNutzerCredentials(nutzer: INutzer): Promise<string> {
    try {
      const data = this.backend.sendRequest("updateNutzerCredentials", {
        nutzer: BackendService.toJsonObj(nutzer),
        sessionId: this.sessionService.session.sessionId
      }, true);
      return data.then(msg => {
        return new Promise<string>((resolve, reject) => {
          if (msg.success === false) {
            reject(msg.error);
          }
          resolve("Änderungen gespeichert");
        })
      });
    } catch (e) {
      return Promise.reject(e.message)
    }
  }

  /** Schritt 1 der TOTP-Einrichtung: Secret + otpauth-URL vom Backend holen */
  public async startTotpSetup(): Promise<{secret: string, otpauthUrl: string}> {
    const result = await this.backend.sendRequest("startTotpSetup", {
      sessionId: this.sessionService.session.sessionId
    }, false);
    if (!result.success) {
      throw result.error || "Fehler beim Starten der Einrichtung";
    }
    return {secret: result.secret, otpauthUrl: result.otpauthUrl};
  }

  /** Schritt 2 der TOTP-Einrichtung: Code bestätigen, damit wird TOTP aktiv */
  public async activateTotp(code: string): Promise<string> {
    const result = await this.backend.sendRequest("activateTotp", {
      sessionId: this.sessionService.session.sessionId,
      code: code
    }, true);
    if (!result.success) {
      throw result.error;
    }
    return "Authenticator-App erfolgreich eingerichtet";
  }

  /** Eigene MFA-Methode auf NONE oder MAIL stellen */
  public async setMfaMethode(methode: 'NONE' | 'MAIL'): Promise<string> {
    const result = await this.backend.sendRequest("setMfaMethode", {
      sessionId: this.sessionService.session.sessionId,
      methode: methode
    }, true);
    if (!result.success) {
      throw result.error;
    }
    return "MFA-Einstellungen gespeichert";
  }

  /** Ob der zweite Faktor für alle Nutzer verpflichtend ist */
  public async getMfaPflicht(): Promise<boolean> {
    const result = await this.backend.sendRequest("getMfaPflicht", {
      sessionId: this.sessionService.session.sessionId
    }, false);
    if (!result.success) {
      throw result.error;
    }
    return !!result.mfaPflicht;
  }

  /** SU/ADM: zweiten Faktor für alle Nutzer erzwingen bzw. wieder freistellen */
  public async setMfaPflicht(aktiv: boolean): Promise<string> {
    const result = await this.backend.sendRequest("setMfaPflicht", {
      sessionId: this.sessionService.session.sessionId,
      aktiv: aktiv
    }, false);
    if (!result.success) {
      throw result.error;
    }
    return aktiv ? "Der zweite Faktor ist jetzt für alle Nutzer verpflichtend" : "Der zweite Faktor ist jetzt freiwillig";
  }

  /** Die eigenen Passkeys für die Verwaltung in den Einstellungen laden */
  public async getPasskeys(): Promise<IPasskey[]> {
    return await this.backend.sendRequest("getPasskeys", {
      sessionId: this.sessionService.session.sessionId
    }, false);
  }

  /** Kompletter Passkey-Registrierungs-Flow: Challenge holen, Browser-Dialog, Ergebnis speichern */
  public async registerPasskey(name: string): Promise<string> {
    const start = await this.backend.sendRequest("startPasskeyRegistration", {
      sessionId: this.sessionService.session.sessionId
    }, false);
    if (!start.success) {
      throw start.error || "Die Passkey-Registrierung konnte nicht gestartet werden";
    }

    const credential = await navigator.credentials.create(toCreateOptions(start.options));

    const result = await this.backend.sendRequest("finishPasskeyRegistration", {
      sessionId: this.sessionService.session.sessionId,
      credential: registrationToJson(credential),
      name: name
    }, false);
    if (!result.success) {
      throw result.error;
    }
    return "Passkey erfolgreich eingerichtet";
  }

  public async deletePasskey(passkeyId: number): Promise<string> {
    const result = await this.backend.sendRequest("deletePasskey", {
      sessionId: this.sessionService.session.sessionId,
      passkeyId: passkeyId
    }, false);
    if (!result.success) {
      throw result.error;
    }
    return "Passkey gelöscht";
  }

  /** SU/ADM: zweiten Faktor eines anderen Nutzers auf E-Mail zurücksetzen */
  public async resetMfaToMail(nutzer: INutzer): Promise<string> {
    const result = await this.backend.sendRequest("resetMfaToMail", {
      sessionId: this.sessionService.session.sessionId,
      nutzerId: nutzer.id
    }, true);
    if (!result.success) {
      throw result.error;
    }
    return "Zweiter Faktor wurde auf E-Mail zurückgesetzt";
  }

  getNutzerListe(): INutzer[] {
    return this.nutzer;
  }

  public getRevision(): number {
    return this.revision;
  }
}
